// ─── dataModel.js ─────────────────────────────────────────────────────────────
// Single source of truth for area data shapes.
// Both internal and external areas now use the same surfaceEntries structure.

import { SITE_ITEMS } from './areaConstants';

// ── Workflow states ──────────────────────────────────────────────────────────
// Project lifecycle: measuring → ready_to_assess → assessing → assessed → approved
export const WORKFLOW = {
  MEASURING:        'measuring',
  READY_TO_ASSESS:  'ready_to_assess',
  ASSESSING:        'assessing',
  ASSESSED:         'assessed',
  COMPARING:        'comparing',
  APPROVED:         'approved',
};

export const WORKFLOW_LABELS = {
  [WORKFLOW.MEASURING]:       { label: 'Measuring in Progress', icon: '📐', color: '#5B2D82' },
  [WORKFLOW.READY_TO_ASSESS]: { label: 'Ready to Assess',      icon: '📋', color: '#E8871B' },
  [WORKFLOW.ASSESSING]:       { label: 'Assessing',            icon: '📋', color: '#d97706' },
  [WORKFLOW.ASSESSED]:        { label: 'Assessed',             icon: '⚖️', color: '#059669' },
  [WORKFLOW.COMPARING]:       { label: 'Comparing Hours',      icon: '⚖️', color: '#7c3aed' },
  [WORKFLOW.APPROVED]:        { label: 'Hours Adjusted',      icon: '✅', color: '#16a34a' },
};

// Review thresholds (percentage difference between measured and assessed hours)
export const REVIEW_THRESHOLDS = { green: 15, amber: 30 };

// ── Area defaults ────────────────────────────────────────────────────────────

const AREA_DEFAULTS = {
  type: 'internal',
  name: '',
  tag: '',
  photos: [],
  surfaceEntries: [],
  repeatGroups: [],
  downtime: 10,
  abseilPct: 0,
  surfaceSettings: {},
  scopeNotes: '',
  scopeExclusions: '',
  defectsNote: '',
  wrapModes: {},
  // Assessment fields (populated by PM in Assess Mode)
  assessHours: null,
  assessNotes: '',
  accessNeeds: [],
  // Approval field (set during Review Mode)
  approvedHours: null,
  // Condition rating per substrate (Poor/Fair/Good) for the Condition Report.
  // Separate from assessBySubstrate so measurer + assessor saves never clobber each other.
  conditionBySubstrate: {},
  // Condition Report v3 — dashboard sort priority. null = use the RAG-derived
  // default (see reportViewModel.priorityFor). Internal only — never shown as text.
  priority: null,
};

// ── Site Items (project-level, counted non-painting scope) ──────────────────
// Site-wide counts recorded once per project — fixtures to supply, fittings to
// replace, things to remove before painting. Stored on the project document
// (not per-area) since these are counted once for the whole site.
export const SITE_ITEMS_DEFAULTS = {
  fingerGuards:         { status: 'counted', count: '', perClientMarkup: null, note: '' },
  doorCaps:             { status: 'counted', count: '', perClientMarkup: null, note: '' },
  kickerPlates:         { status: 'counted', count: '', perClientMarkup: null, note: '' },
  soapDispensers:       { status: 'counted', count: '', note: '' },
  paperTowelDispensers: { status: 'counted', count: '', note: '' },
  toiletRollHolders:    { status: 'counted', count: '', note: '' },
  blinds:               { status: 'counted', count: '', note: '', photos: [] },
  displayBoards:        { status: 'counted', rows: [], note: '' },
  halfHeightDoors:      { status: 'nil',     rows: [] },
  removeReplaceMisc:    { status: 'nil',     rows: [] },
  customItems:          { status: 'nil',     rows: [] },
  representativeRequests: '',
};

// Merge raw siteItems data under the defaults, key by key, so adding a new item
// later doesn't break existing project documents that predate it.
export function hydrateSiteItems(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  Object.keys(SITE_ITEMS_DEFAULTS).forEach(key => {
    const def = SITE_ITEMS_DEFAULTS[key];
    if (key === 'representativeRequests') {
      out[key] = typeof src[key] === 'string' ? src[key] : def;
      return;
    }
    const entry = src[key] && typeof src[key] === 'object' ? src[key] : {};
    out[key] = { ...def, ...entry };
    if (Array.isArray(def.rows)) out[key].rows = Array.isArray(entry.rows) ? entry.rows : [];
    if (Array.isArray(def.photos)) out[key].photos = Array.isArray(entry.photos) ? entry.photos : [];
  });
  return out;
}

// An item counts as "recorded" once the assessor has touched it: its status
// moved off the untouched default, or it carries a count, a non-empty row or a
// note. Used by the Assess picker's "n of 12 recorded" summary.
export function isSiteItemRecorded(key, entry) {
  const def = SITE_ITEMS_DEFAULTS[key];
  if (!def || !entry) return false;
  if (entry.status !== def.status) return true;
  if (typeof entry.count === 'number' && entry.count > 0) return true;
  if (Array.isArray(entry.rows) && entry.rows.some(row =>
    Object.values(row || {}).some(v => (typeof v === 'string' ? v.trim() : v) && v !== row.id)
  )) return true;
  if (typeof entry.note === 'string' && entry.note.trim()) return true;
  return false;
}

export function countRecordedSiteItems(siteItems) {
  return SITE_ITEMS.filter(item => isSiteItemRecorded(item.key, siteItems && siteItems[item.key])).length;
}

// ── Internal Flooring register (project-level, §4) ──────────────────────────
// Floor coverings are graded for replacement, NOT painted — a different thing
// entirely from the app's 'Floors' painting substrate. Project-level like Site
// Items, since a floor covering is scoped by room, not tied to a measured area.
export const FLOORING_DEFAULTS = [];
// each entry:
// { id, product: '', location: '', condition: null, recommendation: '',
//   note: '', safetyRisk: false, safetyNote: '', photos: [] }

// Merge defaults under the raw data so missing fields get safe values
// but existing data is never overwritten.
export function hydrateArea(raw) {
  if (!raw || typeof raw !== 'object') return { ...AREA_DEFAULTS };
  const hydrated = { ...AREA_DEFAULTS, ...raw };
  // Ensure arrays are arrays
  if (!Array.isArray(hydrated.surfaceEntries)) hydrated.surfaceEntries = [];
  if (!Array.isArray(hydrated.repeatGroups)) hydrated.repeatGroups = [];
  if (!Array.isArray(hydrated.photos)) hydrated.photos = [];
  if (!Array.isArray(hydrated.accessNeeds)) hydrated.accessNeeds = [];
  if (!hydrated.conditionBySubstrate || typeof hydrated.conditionBySubstrate !== 'object' || Array.isArray(hydrated.conditionBySubstrate)) hydrated.conditionBySubstrate = {};
  return hydrated;
}

// ── Project defaults (for hydrating project docs) ────────────────────────────

export function hydrateProject(raw) {
  return {
    workflow: WORKFLOW.MEASURING,
    assessedBy: null,
    assessedAt: null,
    approvedAt: null,
    finalReview: null,
    ...raw,
    siteItems: hydrateSiteItems(raw && raw.siteItems),
    flooring: Array.isArray(raw && raw.flooring) ? raw.flooring : [],
    reportMeta: {
      siteName: '', recipientName: '', recipientTitle: '', inspectionDate: '',
      higginsRep: '', siteDirector: '',
      branchName: '', branchAddress: '', branchPhone: '', branchEmail: '', branchAbn: '',
      // Condition Report v3 additions (§8). All optional; empty = fall back / omit.
      assessorName: '', clientContact: '', sector: '',
      overallConditionOverride: '', clientLogo: '', coverPhotoUrl: '',
      keyRisksOverride: [],
      // Stage 1b — personalisation inputs + editable AI-free summary overrides.
      buildingAge: '', notableItems: '',
      overallSummaryOverride: '', sectorSummaryOverride: {}, areaSummaryOverride: {},
      ...(raw && raw.reportMeta ? raw.reportMeta : {}),
    },
  };
}
